/**
 * ProcessHierarchySection
 * 
 * Search result section for Process Hierarchy (EX3)
 * Displays matching processes grouped by cycle
 * Matches exact styling from ProcessHierarchyPage.tsx
 * 
 * NOTE: This is a display-only component. Data is fetched by useGlobalSearch hook.
 */

import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Table, Collapse, Tooltip } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import styled from 'styled-components';
import { Process, CYCLE_COLORS } from '../../../../services/hierarchyService';
import HighlightText from '../components/HighlightText';
import { ProcessHierarchyResult } from '../useGlobalSearch';

const { Panel } = Collapse;

// ========================================
// Styled Components - Match ProcessHierarchyPage.tsx exactly
// ========================================

const SectionContainer = styled.div`
  margin-bottom: 24px;
`;

const SectionHeader = styled.div`
  background: #E0E7FF;
  padding: 12px 16px;
  border-radius: 8px 8px 0 0;
  border: 1px solid #C7D2FE;
  border-bottom: none;
`;

const SectionTitle = styled.h3`
  font-family: Inter;
  font-weight: 600;
  font-size: 14px;
  color: #3730A3;
  margin: 0;
`;

const ContentContainer = styled.div`
  background: white;
  border: 1px solid #E5E7EB;
  border-radius: 0 0 8px 8px;
  overflow: hidden;
`;

const SubSectionTitle = styled.span`
  font-family: Inter;
  font-weight: 500;
  font-size: 13px;
  color: #374151;
`;

const SubSectionCount = styled.span`
  font-family: Inter;
  font-weight: 400;
  font-size: 13px;
  color: #6B7280;
  margin-left: 8px;
`;

const TableWrapper = styled.div`
  .ant-table {
    border-radius: 0;
  }

  .ant-table-thead > tr > th {
    background: #F8FAFC;
    font-family: Inter;
    font-weight: 500;
    font-size: 10px;
    color: #6B7280;
    padding: 6px 8px;
    border-bottom: 1px solid #E5E7EB;
    border-left: 1px solid #E5E7EB;
    
    &:first-child {
      border-left: none;
    }
    
    /* Remove Ant Design's internal column separators */
    &::before {
      display: none !important;
    }
  }

  .ant-table-tbody > tr > td {
    font-family: Inter;
    font-size: 12px;
    color: #1F2937;
    padding: 6px 8px;
    border-bottom: 1px solid #E5E7EB;
    border-left: 1px solid #E5E7EB;
    
    &:first-child {
      border-left: none;
    }
  }

  .ant-table-tbody > tr:last-child > td {
    border-bottom: none;
  }

  .ant-table-tbody > tr:hover > td {
    background: #F9FAFB;
  }
`;

const StyledCollapse = styled(Collapse)`
  border: none;
  background: white;

  .ant-collapse-item {
    border-bottom: 1px solid #E5E7EB;

    &:last-child {
      border-bottom: none;
    }
  }

  .ant-collapse-header {
    padding: 12px 16px !important;
        background: #F8FAFC;

  }

  .ant-collapse-content-box {
    padding: 0 !important;
  }
`;

// Chip components - Match ProcessHierarchyPage.tsx exactly
const ProcessChip = styled.span`
  display: inline-block;
  padding: 4px 12px;
  background: #EFF6FF;
  color: #1E40AF;
  border-radius: 50px;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.3s;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 100%;

  &:hover {
    opacity: 0.8;
    transform: translateY(-1px);
  }
`;

const ProcessOwnerChip = styled.span`
  display: inline-block;
  padding: 4px 12px;
  background: #F8FAFC;
  color: #1F2937;
  border-radius: 50px;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.3s;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 100%;

  &:hover {
    opacity: 0.8;
    transform: translateY(-1px);
  }
`;

const MissingOwnerChip = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 4px 12px;
  background: transparent;
  border: 1px solid #EF4444;
  color: #EF4444;
  border-radius: 50px;
  font-size: 13px;
  font-weight: 500;
  white-space: nowrap;
`;

const RiskChip = styled.span<{ $risk: string }>`
  display: inline-block;
  padding: 4px 12px;
  border-radius: 50px;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.3s;
  background: white;
  white-space: nowrap;

  ${props => {
    switch (props.$risk) {
      case 'Med':
        return `
          border: 1px solid #FACC15;
          color: #FACC15;
        `;
      case 'Low':
        return `
          border: 1px solid #2DD4BF;
          color: #2DD4BF;
        `;
      case 'High':
        return `
          border: 1px solid #EF4444;
          color: #EF4444;
        `;
      case 'Unranked':
      default:
        return `
          border: 1px solid #60A5FA;
          color: #60A5FA;
        `;
    }
  }}

  &:hover {
    opacity: 0.8;
  }

  &::before {
    content: '●';
    margin-right: 6px;
    font-size: 10px;
  }
`;

const ClickableNumber = styled.span`
  color: #1F2937;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;

  &:hover {
    color: #2563EB;
    text-decoration: underline;
  }
`;

const EmptyCell = styled.span`
  color: #9CA3AF;
  font-size: 14px;
`;

// Tooltip styled components - Match ProcessHierarchyPage.tsx exactly
const TooltipTitle = styled.div`
  font-family: Inter;
  font-weight: 600;
  font-size: 16px;
  line-height: 24px;
  color: #1F2937;
  text-align: center;
  margin-bottom: 12px;
`;

const TooltipList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const TooltipItem = styled.div`
  padding: 8px 12px;
  background: #EFF6FF;
  border-radius: 6px;
  font-family: Inter;
  font-weight: 500;
  font-size: 14px;
  line-height: 20px;
  color: #2563EB;
  text-align: center;
  cursor: pointer;
  transition: all 0.3s;

  &:hover {
    background: #DBEAFE;
    transform: translateX(2px);
  }
`;

// ========================================
// Types
// ========================================

export interface ProcessHierarchySectionProps {
  data: ProcessHierarchyResult;
  searchText: string;
}

// ========================================
// Component
// ========================================

const ProcessHierarchySection: React.FC<ProcessHierarchySectionProps> = ({
  data,
  searchText,
}) => {
  const navigate = useNavigate();

  // Navigation handlers
  const handleProcessClick = (process: Process) => {
    navigate('/process-hierarchy', { state: { selectedProcessId: process.id } });
  };

  const handleRiskRankingClick = (process: Process) => {
    if (process.riskAssessmentId) {
      navigate(`/risk-assessment/${process.riskAssessmentId}/tab1`);
    }
  };

  const handleTotalControlsClick = (process: Process) => {
    navigate('/control-activities', { state: { processId: process.id, processCode: process.processCode, processName: process.processName } });
  };

  const handleKeyControlsClick = (process: Process) => {
    navigate('/control-activities', { state: { processId: process.id, processCode: process.processCode, processName: process.processName, isKey: true } });
  };

  const handleGapsClick = (process: Process, severity?: string) => {
    navigate('/gaps', { state: { processId: process.id, riskRating: severity } });
  };

  // Table columns - Match ProcessHierarchyPage.tsx column templates
  const columns: ColumnsType<Process> = [
    {
      title: 'Process',
      dataIndex: 'processCode',
      key: 'process',
      width: 'auto',
      render: (_: string, record: Process) => (
        <div style={{ display: 'flex', flexWrap: 'nowrap', overflow: 'hidden' }}>
          <ProcessChip onClick={() => handleProcessClick(record)}>
            <HighlightText text={`${record.processCode} ${record.processName}`} searchText={searchText} />
          </ProcessChip>
        </div>
      ),
    },
    {
      title: 'Process Owner',
      dataIndex: 'processOwner',
      key: 'processOwner',
      width: 160,
      render: (owner: string, record: Process) => {
        if (!owner || owner.trim() === '') {
          return (
            <MissingOwnerChip>
              Missing Owner
            </MissingOwnerChip>
          );
        }
        return (
          <div style={{ display: 'flex', flexWrap: 'nowrap', overflow: 'hidden' }}>
            <ProcessOwnerChip onClick={() => handleProcessClick(record)}>
              <HighlightText text={owner} searchText={searchText} />
            </ProcessOwnerChip>
          </div>
        );
      },
    },
    {
      title: 'Risk Ranking',
      dataIndex: 'riskRanking',
      key: 'riskRanking',
      width: 120,
      render: (riskRanking: string, record: Process) => (
        <RiskChip $risk={riskRanking} onClick={() => handleRiskRankingClick(record)}>
          {riskRanking}
        </RiskChip>
      ),
    },
    {
      title: '# Total Controls',
      dataIndex: 'totalControls',
      key: 'totalControls',
      width: 112,
      align: 'center',
      render: (count: number, record: Process) => (
        <ClickableNumber onClick={() => handleTotalControlsClick(record)}>
          {count}
        </ClickableNumber>
      ),
    },
    {
      title: '# Key Controls',
      dataIndex: 'keyControls',
      key: 'keyControls',
      width: 108,
      align: 'center',
      render: (count: number, record: Process) => (
        <ClickableNumber onClick={() => handleKeyControlsClick(record)}>
          {count}
        </ClickableNumber>
      ),
    },
    {
      title: 'Testing Status',
      dataIndex: 'testingStatus',
      key: 'testingStatus',
      width: 100,
      align: 'center',
      render: (status: number) => {
        const value = typeof status === 'number' ? status : 0;
        return `${value.toFixed(2)}%`;
      },
    },
    {
      title: 'Total Gaps',
      dataIndex: 'totalGaps',
      key: 'totalGaps',
      width: 120,
      align: 'center',
      render: (_: any, record: Process) => {
        const totalGaps = record.totalGaps || record.gaps?.reduce((sum, gap) => sum + gap.count, 0) || 0;
        
        if (!totalGaps || totalGaps === 0) {
          return <EmptyCell>-</EmptyCell>;
        }

        // Build tooltip content showing breakdown by severity
        const tooltipContent = record.gaps && record.gaps.length > 0 ? (
          <div>
            <TooltipTitle>GAPs Breakdown</TooltipTitle>
            <TooltipList>
              {record.gaps.map((gap) => (
                <TooltipItem
                  key={gap.severity}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleGapsClick(record, gap.severity);
                  }}
                >
                  {gap.severity}: {gap.count}
                </TooltipItem>
              ))}
            </TooltipList>
          </div>
        ) : null;

        const gapsContent = (
          <span
            onClick={() => handleGapsClick(record)}
            style={{
              backgroundColor: '#F3F4F6',
              color: '#6B7280',
              padding: '4px 10px',
              borderRadius: '12px',
              fontSize: '12px',
              fontWeight: 500,
              cursor: 'pointer',
              transition: 'all 0.2s ease',
            }}
          >
            Total - {totalGaps}
          </span>
        );

        if (tooltipContent) {
          return (
            <Tooltip
              title={tooltipContent}
              placement="top"
              overlayStyle={{ maxWidth: '300px' }}
              overlayInnerStyle={{
                backgroundColor: '#FFFFFF',
                padding: '16px',
                borderRadius: '12px',
                boxShadow: '0px 4px 20px rgba(0, 0, 0, 0.15)',
              }}
              color="#FFFFFF"
            >
              {gapsContent}
            </Tooltip>
          );
        }

        return gapsContent;
      },
    },
  ];

  // Calculate total results
  const totalResults = data.cycles.reduce((sum, cycle) => sum + cycle.processes.length, 0);

  // Don't render if no results
  if (data.cycles.length === 0) {
    return null;
  }

  return (
    <SectionContainer>
      <SectionHeader>
        <SectionTitle>Process Hierarchy ({totalResults} results)</SectionTitle>
      </SectionHeader>
      
      <ContentContainer>
        <StyledCollapse defaultActiveKey={data.cycles.map(c => c.id)}>
          {data.cycles.map((cycle, index) => {
            const bgColor = CYCLE_COLORS[index % CYCLE_COLORS.length];
            return (
              <Panel
                key={cycle.id}
                header={
                  <>
                    <SubSectionTitle>{cycle.cycleCode} - {cycle.cycleName}</SubSectionTitle>
                    <SubSectionCount>({cycle.processes.length})</SubSectionCount>
                  </>
                }
                style={{ background: bgColor }}
              >
                <TableWrapper>
                  <Table
                    columns={columns}
                    dataSource={cycle.processes}
                    rowKey="id"
                    pagination={false}
                    size="small"
                  />
                </TableWrapper>
              </Panel>
            );
          })}
        </StyledCollapse>
      </ContentContainer>
    </SectionContainer>
  );
};

export default ProcessHierarchySection;
